// src/services/workspaceService.js
import {
  collection, doc, setDoc, getDoc, updateDoc, getDocs,
  query, where, orderBy, serverTimestamp, onSnapshot, increment
} from 'firebase/firestore';
import { db } from './firebase';
import { getPlanConfig } from '../config/plans';

const WORKSPACES_COL = 'workspaces';

/**
 * Crear un workspace para una nueva empresa
 */
export const createWorkspace = async (ownerId, workspaceData) => {
  const planConfig = getPlanConfig('free');
  const workspaceRef = doc(db, WORKSPACES_COL, ownerId);

  // Modulos segun plan free
  const modules = {};
  const allModuleKeys = ['gestorPro', 'generador', 'tareas', 'calendario', 'agenteIA', 'horasExtra', 'ventas'];
  allModuleKeys.forEach(key => {
    modules[key] = planConfig.modules.includes(key);
  });

  const workspace = {
    name: workspaceData.name,
    ruc: workspaceData.ruc || '',
    phone: workspaceData.phone || '',
    country: workspaceData.country || '',
    industry: workspaceData.industry || '',
    size: workspaceData.size || '1-10',
    logo: workspaceData.logo || '',
    ownerId,
    ownerEmail: workspaceData.ownerEmail || '',
    status: 'pending',
    plan: 'free',
    maxUsers: planConfig.maxUsers,
    modules,
    memberCount: 1,
    onboardingCompleted: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  await setDoc(workspaceRef, workspace);
  return { id: ownerId, ...workspace };
};

/**
 * Obtener un workspace por ID
 */
export const getWorkspace = async (workspaceId) => {
  const snap = await getDoc(doc(db, WORKSPACES_COL, workspaceId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
};

/**
 * Actualizar datos del workspace
 */
export const updateWorkspace = async (workspaceId, data) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Completar onboarding
 */
export const completeOnboarding = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    onboardingCompleted: true,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Obtener TODOS los workspaces (para super-admin)
 */
export const getAllWorkspaces = async () => {
  const q = query(collection(db, WORKSPACES_COL), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Suscripción en tiempo real a todos los workspaces (super-admin)
 */
export const subscribeToWorkspaces = (callback) => {
  const q = query(collection(db, WORKSPACES_COL), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    callback(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

/**
 * Aprobar workspace (super-admin)
 */
export const approveWorkspace = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    status: 'active',
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  // También activar al owner
  await updateDoc(doc(db, 'users', workspaceId), {
    status: 'active',
    updatedAt: serverTimestamp(),
  });
};

/**
 * Rechazar workspace (super-admin)
 */
export const rejectWorkspace = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    status: 'rejected',
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'users', workspaceId), {
    status: 'rejected',
    updatedAt: serverTimestamp(),
  });
};

/**
 * Suspender workspace (super-admin)
 */
export const suspendWorkspace = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    status: 'suspended',
    updatedAt: serverTimestamp(),
  });
};

/**
 * Obtener workspaces pendientes
 */
export const getPendingWorkspaces = async () => {
  const q = query(
    collection(db, WORKSPACES_COL),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Incrementar memberCount del workspace
 */
export const incrementMemberCount = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    memberCount: increment(1),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Decrementar memberCount del workspace
 */
export const decrementMemberCount = async (workspaceId) => {
  await updateDoc(doc(db, WORKSPACES_COL, workspaceId), {
    memberCount: increment(-1),
    updatedAt: serverTimestamp(),
  });
};
